import React, { useEffect, useState } from 'react';
import {
  TextField,
  MenuItem,
  Button,
  Tooltip,
  CircularProgress,
  Box,
  Typography,
  InputAdornment,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  TablePagination
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import { fetchCompanies, fetchInvoiceById } from '../api/billingInvoicingApi';
import {
  useBillingInvoicing,
  BillingCompany
} from '../context/BillingInvoicingContext';
import { get } from '../../../services/api';
import SuspenseLoader from 'src/components/SuspenseLoader';

const CompanySelection: React.FC = () => {
  const { t } = useTranslation();
  const { selectedCompany, setSelectedCompany, setCurrentStep, setInvoice } =
    useBillingInvoicing();

  const getStatusTranslation = (status: string) => {
    const statusKeyMap: Record<string, string> = {
      APPROVED: 'approved',
      REJECTED: 'rejected',
      PENDING_APPROVAL: 'underReview',
      CHANGES_REQUESTED: 'changes_requested',
      FINALIZED: 'approved',
      SENT: 'approved'
    };
    const key = statusKeyMap[status] || status.toLowerCase();
    return t(`billing.invoice_details.status.${key}`, status) as string;
  };
  const [companies, setCompanies] = useState<BillingCompany[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [invoices, setInvoices] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(5);
  const [sortBy, setSortBy] = useState('invoiceId');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [loadingInvoices, setLoadingInvoices] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetchCompanies()
      .then(setCompanies)
      .catch(() =>
        setError(
          t(
            'billing.company_selection.error_loading',
            'Failed to load companies'
          )
        )
      )
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(() => {
    if (
      selectedCompany &&
      !companies.some((c) => c.id === selectedCompany.id)
    ) {
      setSelectedCompany(null);
    }
  }, [companies, selectedCompany, setSelectedCompany]);

  useEffect(() => {
    if (!selectedCompany) return;
    setLoadingInvoices(true);
    get(`/v1/prt/${selectedCompany.id}/invoices?rtuId=${selectedCompany.id}`)
      .then(setInvoices)
      .finally(() => setLoadingInvoices(false));
  }, [selectedCompany]);

  const filtered = companies.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
  };

  const sortedInvoices = [...invoices].sort((a, b) => {
    let aValue = a[sortBy];
    let bValue = b[sortBy];
    if (typeof aValue === 'string') aValue = aValue.toLowerCase();
    if (typeof bValue === 'string') bValue = bValue.toLowerCase();
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const paginatedInvoices = sortedInvoices.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box mt={4}>
      {loadingInvoices && (
        <SuspenseLoader
          message={t(
            'billing.company_selection.loading_invoices',
            'Loading invoices...'
          )}
        />
      )}
      <Box display="flex" alignItems="center" mb={1}>
        <Typography variant="h6">
          {t('billing.company_selection.title', 'Select Company (RTU)')}
        </Typography>
        <Tooltip
          title={t(
            'billing.company_selection.tooltip',
            'Choose the company (RTU) for which you want to generate billing and invoices.'
          )}
          placement="right"
        >
          <IconButton size="small" sx={{ ml: 1 }}>
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <TextField
        label={t('billing.company_selection.search_label', 'Search Company')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        fullWidth
        margin="normal"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          )
        }}
      />
      {loading ? (
        <Box display="flex" justifyContent="center" my={2}>
          <CircularProgress size={24} />
        </Box>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <TextField
          select
          label={t('billing.company_selection.company_label', 'Company')}
          value={
            selectedCompany &&
            companies.some((c) => c.id === selectedCompany.id)
              ? selectedCompany.id
              : ''
          }
          onChange={(e) => {
            const company = companies.find((c) => c.id === e.target.value);
            setSelectedCompany(company || null);
          }}
          fullWidth
          margin="normal"
        >
          {filtered.map((company) => (
            <MenuItem key={company.id} value={company.id}>
              {company.name}
            </MenuItem>
          ))}
        </TextField>
      )}
      {/* Single Invoice Table (always shown for selected company) */}
      {selectedCompany && (
        <Box mt={4}>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'invoiceId'}
                      direction={sortBy === 'invoiceId' ? sortDirection : 'asc'}
                      onClick={() => handleSort('invoiceId')}
                    >
                      {t('billing.invoice_creation.table.invoice_id')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'companyName'}
                      direction={
                        sortBy === 'companyName' ? sortDirection : 'asc'
                      }
                      onClick={() => handleSort('companyName')}
                    >
                      {t('billing.invoice_creation.table.company_name')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'grossTotal'}
                      direction={
                        sortBy === 'grossTotal' ? sortDirection : 'asc'
                      }
                      onClick={() => handleSort('grossTotal')}
                    >
                      {t('billing.invoice_creation.table.total')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'createdByName'}
                      direction={
                        sortBy === 'createdByName' ? sortDirection : 'asc'
                      }
                      onClick={() => handleSort('createdByName')}
                    >
                      {t('billing.invoice_creation.table.created_by')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'status'}
                      direction={sortBy === 'status' ? sortDirection : 'asc'}
                      onClick={() => handleSort('status')}
                    >
                      {t('billing.invoice_creation.table.status')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedInvoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      {t('No invoices found.', 'No invoices found.')}
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedInvoices.map((inv) => (
                    <TableRow key={inv.invoiceId || inv.id}>
                      <TableCell>{inv.invoiceId || inv.id}</TableCell>
                      <TableCell>{selectedCompany.name}</TableCell>
                      <TableCell>
                        {inv.grossTotal != null
                          ? `€${inv.grossTotal.toFixed(2)}`
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {inv.createdByName || inv.createdBy || '-'}
                      </TableCell>
                      <TableCell>
                        {inv.status ? getStatusTranslation(inv.status) : '-'}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={async () => {
                            const invoice = await fetchInvoiceById(
                              selectedCompany.id,
                              inv.invoiceId || inv.id
                            );
                            setInvoice(invoice);
                            setCurrentStep('invoice-details');
                          }}
                        >
                          {t('billing.invoice_creation.table.view')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={sortedInvoices.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[5]}
            labelRowsPerPage={t('common.rows_per_page')}
          />
        </Box>
      )}
      <Box mt={2} display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          color="primary"
          disabled={!selectedCompany}
          onClick={() => setCurrentStep('order-selection')}
        >
          {t('billing.company_selection.next', 'Next')}
        </Button>
      </Box>
    </Box>
  );
};

export default CompanySelection;
